import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/axiosConfig';
import FinancialsFiltersModal from '../../components/FinancialsFiltersModal/FinancialsFiltersModal';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import useCompanyFilter from '../../hooks/useCompanyFilter';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import { formatReportMoney } from '../../utils/financialReportUtils';
import { getDefaultFinancialsDateRange } from '../../utils/financialsDateRange';
import './FinancialsDashboard.css';
import '../../components/FinancialsFiltersModal/FinancialsFiltersModal.css';

function ClientReports() {
  const defaultRange = getDefaultFinancialsDateRange();
  const { isLoading, withLoading } = usePageLoading(true);
  const {
    companies,
    selectedCompanyId,
    setSelectedCompanyId,
    companyQueryParam,
    companiesLoaded,
  } = useCompanyFilter();
  const [fromDate, setFromDate] = useState(defaultRange.fromDate);
  const [toDate, setToDate] = useState(defaultRange.toDate);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [clientReportLoading, setClientReportLoading] = useState(false);
  const [clientReport, setClientReport] = useState(null);
  const [lookupLoaded, setLookupLoaded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const fetchClientReport = useCallback(async (rangeFrom, rangeTo, clientId, projectId, companyParams) => {
    const params = {
      fromDate: rangeFrom,
      toDate: rangeTo,
      ...companyParams,
    };

    if (projectId) {
      params.projectId = Number(projectId);
    }
    if (clientId) {
      params.clientId = Number(clientId);
    }

    const response = await api.get('/financials/client-report', { params });
    setClientReport(response.data);
  }, []);

  useEffect(() => {
    withLoading(async () => {
      const clientsRes = await api.get('/clients');
      setClients(clientsRes.data);

      const projectsRes = await api.get('/projects');
      setProjects(projectsRes.data);
      setLookupLoaded(true);
    }).catch((error) => {
      const message = error.response?.data?.Error || error.message || 'Error loading clients and projects';
      alert(message);
    });
  }, [withLoading]);

  useEffect(() => {
    if (!lookupLoaded || !companiesLoaded) {
      return;
    }

    if (!selectedClientId && !selectedProjectId) {
      setClientReport(null);
      return;
    }

    setClientReportLoading(true);
    fetchClientReport(fromDate, toDate, selectedClientId, selectedProjectId, companyQueryParam)
      .catch((error) => {
        const message = error.response?.data?.Error || error.message || 'Error loading client report';
        alert(message);
      })
      .finally(() => {
        setClientReportLoading(false);
      });
  }, [
    lookupLoaded,
    companiesLoaded,
    fromDate,
    toDate,
    selectedClientId,
    selectedProjectId,
    selectedCompanyId,
    fetchClientReport,
  ]);

  const clientReportScopeLabel = useMemo(() => {
    if (!clientReport) {
      return '';
    }
    if (clientReport.projectName) {
      return clientReport.projectName;
    }
    if (clientReport.clientName) {
      return `${clientReport.clientName} (all projects)`;
    }
    return 'Selected client or project';
  }, [clientReport]);

  const companyLabel = useMemo(() => {
    if (!selectedCompanyId) {
      return 'All companies';
    }
    return companies.find((company) => String(company.id) === String(selectedCompanyId))?.name || 'Company';
  }, [companies, selectedCompanyId]);

  const clientLabel = useMemo(() => {
    if (selectedProjectId) {
      const project = projects.find((item) => String(item.id) === String(selectedProjectId));
      return project?.name || 'Project';
    }
    if (selectedClientId) {
      const client = clients.find((item) => String(item.id) === String(selectedClientId));
      return client?.name || 'Client';
    }
    return null;
  }, [clients, projects, selectedClientId, selectedProjectId]);

  const handleApplyFilters = (filters) => {
    setFromDate(filters.fromDate);
    setToDate(filters.toDate);
    setSelectedCompanyId(filters.companyId || '');
    setSelectedClientId(filters.clientId || '');
    setSelectedProjectId(filters.projectId || '');
  };

  const handleClearFilters = (filters) => {
    handleApplyFilters(filters);
  };

  return (
    <PageLoadingArea isLoading={isLoading}>
      <div className="financials-dashboard">
        <div className="financials-dashboard-header">
          <div>
            <h1>Client Reports</h1>
            <p className="financials-dashboard-subtitle">
              Client-wise bill totals and outstanding amounts
            </p>
            <div className="financials-active-filters">
              <span className="financials-active-filter-chip">
                {formatDateDDMMYYYY(fromDate)} – {formatDateDDMMYYYY(toDate)}
              </span>
              {clientLabel && (
                <span className="financials-active-filter-chip">{clientLabel}</span>
              )}
              {companies.length > 0 && (
                <span className="financials-active-filter-chip">{companyLabel}</span>
              )}
            </div>
          </div>
          <div className="financials-dashboard-header-actions">
            <button
              type="button"
              className="financials-filter-trigger"
              onClick={() => setShowFilters(true)}
            >
              Filters
            </button>
          </div>
        </div>

        {!selectedClientId && !selectedProjectId && (
          <section className="financials-chart-card financials-empty-state">
            <h2>Client report</h2>
            <p className="financials-chart-hint">
              Open filters and select a client or project to view bill totals for the chosen date range.
            </p>
            <button
              type="button"
              className="financials-filter-trigger"
              onClick={() => setShowFilters(true)}
            >
              Open filters
            </button>
          </section>
        )}

        {(selectedClientId || selectedProjectId) && (
          <section className="financials-stat-section">
            <div className="financials-client-report-heading">
              <h2>{clientReportScopeLabel || 'Loading report...'}</h2>
              {clientReport && (
                <p>{clientReport.billsCount} bill{clientReport.billsCount === 1 ? '' : 's'} in this period</p>
              )}
            </div>

            {clientReportLoading ? (
              <div className="financials-client-report-loading">Loading report...</div>
            ) : clientReport && (
              <div className="financials-stat-grid financials-stat-grid-client-report">
                <article className="financials-stat-card bills">
                  <div className="financials-stat-label">Bills (excl. GST)</div>
                  <div className="financials-stat-value">{formatReportMoney(clientReport.billsBaseTotal)}</div>
                  <div className="financials-stat-meta">Base bill amount</div>
                </article>
                <article className="financials-stat-card gst">
                  <div className="financials-stat-label">GST amount</div>
                  <div className="financials-stat-value">{formatReportMoney(clientReport.billsGstTotal)}</div>
                  <div className="financials-stat-meta">Total GST on bills</div>
                </article>
                <article className="financials-stat-card bills-inc-gst">
                  <div className="financials-stat-label">Bills incl. GST</div>
                  <div className="financials-stat-value">{formatReportMoney(clientReport.billsIncGstTotal)}</div>
                  <div className="financials-stat-meta">Invoice totals with GST</div>
                </article>
                <article className="financials-stat-card income">
                  <div className="financials-stat-label">Income</div>
                  <div className="financials-stat-value">
                    {formatReportMoney(clientReport.incomeTotal ?? clientReport.amountReceivedTotal)}
                  </div>
                  <div className="financials-stat-meta">
                    {clientReport.incomeCount} entr{clientReport.incomeCount === 1 ? 'y' : 'ies'} in this period
                  </div>
                </article>
                <article className="financials-stat-card outstanding">
                  <div className="financials-stat-label">Outstanding</div>
                  <div className="financials-stat-value">{formatReportMoney(clientReport.outstandingTotal)}</div>
                  <div className="financials-stat-meta">Bills incl. GST minus income</div>
                </article>
              </div>
            )}
          </section>
        )}
      </div>

      <FinancialsFiltersModal
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        mode="client-report"
        values={{
          fromDate,
          toDate,
          companyId: selectedCompanyId,
          clientId: selectedClientId,
          projectId: selectedProjectId,
        }}
        companies={companies}
        clients={clients}
        projects={projects}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
      />
    </PageLoadingArea>
  );
}

export default ClientReports;
